'use client'

import React from 'react'

import { Box, Text } from '@chakra-ui/react'

import {
  sortArrowColors,
  sortBadgeTextStyles,
  sortIndicatorActiveContainerStyles,
  sortIndicatorContainerStyles,
} from './Table.styles'
import { useTableColorScheme } from './TableContext'

export type SortOrder = 'asc' | 'desc' | 'none' | 'default'

type SortIndicatorProps = {
  /**
   * The current sort direction.
   * - 'asc' / 'desc': active sort, single chevron in text/primary
   * - 'default': designated default column at rest — both chevrons always visible in icon/secondary
   * - 'none': sortable but not active — both chevrons visible only on hover
   */
  order: SortOrder
  /** When true and order is 'none', renders both chevrons to signal sortability. */
  isParentHovered?: boolean
  /** Small numeral shown next to the chevrons (e.g. multi-key sort order) when order is 'asc'/'desc'. */
  badge?: 1 | 2
}

const UpChevron = ({ color }: { color: string }) => (
  <svg
    fill='none'
    height='7'
    viewBox='0 0 12 7'
    width='12'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M1 6L6 1L11 6'
      stroke={color}
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='1.5'
    />
  </svg>
)

const DownChevron = ({ color }: { color: string }) => (
  <svg
    fill='none'
    height='7'
    viewBox='0 0 12 7'
    width='12'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M1 1L6 6L11 1'
      stroke={color}
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='1.5'
    />
  </svg>
)

export const SortIndicator = ({
  order,
  isParentHovered = false,
  badge,
}: SortIndicatorProps) => {
  const colorScheme = useTableColorScheme()
  const { active, inactive } = sortArrowColors(colorScheme)

  // An active sort has one definite direction — render a single centered chevron
  // rather than reserving space for both, which left the visible one off-center.
  if (order === 'asc' || order === 'desc') {
    const Chevron = order === 'asc' ? UpChevron : DownChevron
    return (
      <Box {...sortIndicatorActiveContainerStyles}>
        <Chevron color={active} />
        {badge && (
          <Text color={active} {...sortBadgeTextStyles}>
            {badge}
          </Text>
        )}
      </Box>
    )
  }

  // 'none': both slots visible only on hover. 'default': always visible, muted.
  const containerVisibility =
    order === 'none' && !isParentHovered ? 'hidden' : 'visible'

  return (
    <Box
      {...sortIndicatorContainerStyles}
      style={{ visibility: containerVisibility }}
    >
      <UpChevron color={inactive} />
      <DownChevron color={inactive} />
    </Box>
  )
}
